import { FormControl, FormLabel, Table, TableBody, TableCell, TableRow } from '@mui/material'
import styled from 'styled-components'

const DetailPanel = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
  border-top: 0.25rem solid ${({ theme }) => theme.notificator.wgColorLow};
`

const PanelHeading = styled.h1`
  font-size: ${({ theme }) => theme.notificator.wgTextBigger};
  font-weight: bold;
  margin-top: 0;
`

const Message = styled.div`
  font-size: ${({ theme }) => theme.notificator.wgTextSmaller};
`

const Image = styled.img`
  margin-top: 1rem;
  max-width: 291px;
  width: 100%;
`

function Detail({ onHide, mode }: { onHide: () => void; mode: 'general' | 'selectors' }) {
  const imageLocation = `/microapps/notificator/assets/help_images`

  return (
    <DetailPanel onClick={onHide}>
      <PanelHeading>How To Use Notificator</PanelHeading>
      <Message>
        This app can be used to send examples of smart notifications available in the various Workgrid catalog apps and
        Workshop templates. It is for demo purposes only and is not tied to any back end systems.
      </Message>
      <Table data-testid={'GeneralHelp'} style={mode === 'general' ? { display: 'block' } : { display: 'none' }}>
        <TableBody>
          <TableRow>
            <TableCell>
              <FormControl>
                <FormLabel>Send Cards To</FormLabel>
                <Message>
                  This is the user who will receive the notification. It defaults to the logged in user, but can be set
                  to the email address of any user in the space you are running the app in.
                </Message>
              </FormControl>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <FormControl>
                <FormLabel>Use Submitter Name</FormLabel>
                <Message>
                  This name will appear in notifications which list the name of the person who submitted whatever
                  triggered the notification. For example, the person who submitted an expense report on an expense
                  approval notification that the approver is receiving.
                </Message>
                <Image src={imageLocation + '/example_submitter.png'} />
              </FormControl>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <FormControl>
                <FormLabel>Use Approver Name</FormLabel>
                <Message>
                  This name will appear in notifications which list the name of the person who is assigned to approve
                  whatever triggered the notification. For example, the person who has or will approve an expense report
                  on an expense status notification that the submitter is receiving.
                </Message>
                <Image src={imageLocation + '/example_approver.png'} />
              </FormControl>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <FormControl>
                <FormLabel>Days before card expires (1 - 365)</FormLabel>
                <Message>
                  This will set how many days the notification will appear in the toolbar before being automatically
                  deleted.
                </Message>
              </FormControl>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Table data-testid={'SelectorsHelp'} style={mode === 'selectors' ? { display: 'block' } : { display: 'none' }}>
        <TableBody>
          <TableRow>
            <TableCell>
              <FormControl>
                <FormLabel>Send</FormLabel>
                <Message>Select card(s) to send</Message>
                <Image src={imageLocation + '/ui_cardOptions.png'} />
                <ul>
                  <li>
                    <strong>All # Notifications</strong> will send every example card available using the default system
                    name and icon. The # will indicate how many cards that currently is.
                  </li>
                  <li>
                    <strong>An Individual Notification</strong> will allow you to select an individual card to send. You
                    will also have the option of customizing the system and icon used by the card.
                  </li>
                  <li>
                    <strong>All Notifications from a Data Source</strong> will allow you to send all the cards related
                    to a specific data source or system. For example, all the cards available for ServiceNow.
                  </li>
                  <li>
                    <strong>All Notifications for a Use Case</strong> will allow you to send all cards related to a
                    specific use case. For example, all the cards related to Expense Report Status Updates.
                  </li>
                  <li>
                    <strong>All Notifications of a Type</strong> will allow you to select all cards that go to a
                    specific toolbar location (ie, To Know or To Do).
                  </li>
                  <li>
                    <strong>All Notifications from a Preset Group</strong> will allow you to send a predefined group of
                    cards. For example, you might have a group defined for setting up a demo for conferences. If you
                    need to create a new group, ask Workgrid Customer success to set it up for you.
                  </li>
                </ul>
              </FormControl>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <FormControl>
                <FormLabel>
                  Use Source Name
                  {' / '}
                  Use Source Icon
                </FormLabel>
                <Message>
                  If you select an individual notification or all notifications from a use case, you will be able to
                  customize the Source Name that shows on the card and select from a limited list of alternative icons.
                  This is useful when you want to show a specific system or you know a customer calls a given system
                  something different internally. The icons represent systems for which Workgrid has already built a
                  template (marked as Currently Available) or for which Workgrid has researched and found to have the
                  APIs necessary to build an app in that use case (marked as Potentially Available).
                </Message>
                <Image src={imageLocation + '/ui_customSource.png'} />
              </FormControl>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <FormControl>
                <Message>
                  Once you have made your selections, you will see a list of all the cards which will be sent when you
                  hit submit, with example images. The example images will not be updated to reflect your
                  customizations.
                </Message>
                <Image src={imageLocation + '/ui_selectedDetails.png'} />
              </FormControl>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </DetailPanel>
  )
}

export default Detail
